import { matchPath } from 'react-router-dom';

// One human title per route (WCAG 2.4.2). It is both the document.title prefix and the
// text of the polite route-change live region (hooks/useRouteAnnouncer.js).
//
// Every non-redirect <Route> in App.js needs an entry here; routeTitles.test.js parses
// App.js and fails on a missing, duplicated or stale one. Redirect routes (<Navigate>)
// are deliberately absent: the destination route's title is what the user lands on.
// The catch-all "*" route is the 404 and is covered by NOT_FOUND_TITLE.
//
// Patterns use react-router syntax and are matched with `end: true`. Titles follow the
// SidebarNavigation.js labels, section-prefixed where two sections share a label (Etsy /
// Shopify / CraftFlow "Orders"). This is the single copy of each title: TopNavigation.js
// reads the Etsy and Account ones from getRouteTitle() rather than repeating them.
export const APP_NAME = 'CopilotPrinter';
export const NOT_FOUND_TITLE = 'Page not found';

export const ROUTE_TITLES = [
  // Public
  ['/', 'Home'],
  ['/login', 'Sign in'],
  ['/oauth/redirect', 'Connecting your account'],
  ['/account-suspended', 'Account suspended'],
  ['/docs/ai-agents', 'AI Agent Setup Guide'],

  // Workspace
  ['/mockup-creator', 'Mockup Creator'],
  ['/mockups/recreate', 'Regenerate jobs'],
  ['/mockups/recreate/:jobId', 'Regenerate job'],
  ['/ai-design', 'AI Design Studio'],
  ['/admin/credits', 'AI Credits'],
  ['/tools', 'Tools'],
  ['/account', 'Account Settings'],
  ['/printers', 'Printers'],
  ['/admin', 'Admin Dashboard'],
  ['/templates/create', 'Create Product Template'],

  // Etsy
  ['/etsy/dashboard', 'Etsy Dashboard'],
  ['/etsy/orders', 'Etsy Orders'],
  ['/etsy/customers', 'Etsy Customers'],
  ['/etsy/analytics', 'Etsy Analytics'],
  ['/etsy/listings', 'Etsy Listings'],

  // Shopify
  ['/shopify/connect', 'Connect Shopify'],
  ['/shopify/success', 'Shopify Connected'],
  ['/shopify/dashboard', 'Shopify Dashboard'],
  ['/shopify/products', 'Shopify Products'],
  ['/shopify/orders', 'Shopify Orders'],
  ['/shopify/analytics', 'Shopify Analytics'],
  ['/shopify/templates/create', 'Create Shopify Template'],
  ['/shopify/templates/:id/edit', 'Edit Shopify Template'],
  ['/shopify/migration/exceptions', 'Shopify Migration Exceptions'],

  // CraftFlow Commerce
  ['/craftflow/dashboard', 'CraftFlow Dashboard'],
  ['/craftflow/analytics', 'CraftFlow Analytics'],
  ['/craftflow/products', 'CraftFlow Products'],
  ['/craftflow/products/create', 'Create CraftFlow Product'],
  ['/craftflow/products/edit/:id', 'Edit CraftFlow Product'],
  ['/craftflow/templates/create', 'Create CraftFlow Template'],
  ['/craftflow/collections', 'Collections'],
  ['/craftflow/orders', 'CraftFlow Orders'],
  ['/craftflow/orders/:id', 'CraftFlow Order Details'],
  ['/craftflow/customers', 'CraftFlow Customers'],
  ['/craftflow/customers/:id', 'CraftFlow Customer Details'],
  ['/craftflow/messages', 'Messages'],
  ['/craftflow/discounts', 'Discounts'],
  ['/craftflow/sales', 'Sales'],
  ['/craftflow/gift-cards', 'Gift Cards'],
  ['/craftflow/price-lists', 'Wholesale Pricing'],
  ['/craftflow/prospects', 'Wholesale Prospects'],
  ['/craftflow/invoices', 'Invoices'],
  ['/craftflow/reviews', 'Reviews'],
  ['/craftflow/gangsheet-builder', 'Gangsheet Builder'],
  ['/craftflow/builder-settings', 'Gangsheet Builder Settings'],
  ['/craftflow/gang-sheets', 'Gang Sheet Orders'],
  ['/craftflow/gang-sheets/:id', 'Gang Sheet Order Details'],
  ['/craftflow/gallery', 'Gallery Assets'],
  ['/craftflow/export-jobs', 'Export Jobs'],
  ['/craftflow/pages', 'Pages'],
  ['/craftflow/pages/new', 'New Page'],
  ['/craftflow/pages/:id/edit', 'Edit Page'],
  ['/craftflow/emails', 'Emails'],
  ['/craftflow/emails/templates/new', 'New Email Template'],
  ['/craftflow/emails/templates/edit/:id', 'Edit Email Template'],
  ['/craftflow/marketing/assets', 'Marketing Assets'],
  ['/craftflow/settings', 'Storefront Settings'],

  // Agent Experience (agent-facing, not in the sidebar)
  ['/ax/skills', 'Agent Skills'],
  ['/ax/contracts', 'Agent Contracts'],
  ['/ax/rules', 'Agent Rules'],
  ['/ax/artifacts', 'Agent Artifacts'],

  // Subscription
  ['/subscription', 'Subscription'],
  ['/subscription/checkout', 'Subscription Checkout'],
  ['/subscription/success', 'Payment Successful'],
  ['/subscription/canceled', 'Payment Canceled'],
];

// The title of the route that renders for `pathname` (no app-name suffix).
export function getRouteTitle(pathname) {
  for (const [path, title] of ROUTE_TITLES) {
    if (matchPath({ path, end: true }, pathname)) return title;
  }
  return NOT_FOUND_TITLE;
}

export function formatDocumentTitle(title) {
  return `${title} | ${APP_NAME}`;
}
